import { UserManager, WebStorageStateStore, type User } from 'oidc-client-ts';
import { IDENTITY_URL, IS_SUPERVISED } from '../env';
import { SupervisedUserManager } from '../factory/supervisedUserManager';

const base = window.location.origin;

const standaloneUserManager = new UserManager({
    authority: IDENTITY_URL,
    client_id: 'ComptabiliteAI',
    redirect_uri: `${base}/signin-callback`,
    post_logout_redirect_uri: `${base}/signout-callback`,
    response_type: 'code',
    scope: 'openid profile email acceoConnectApi',
    automaticSilentRenew: false,
    // localStorage instead of the default sessionStorage so the OIDC user object survives
    // new tabs and windows. Without this, getUser() returns null in a new tab even though
    // the AcceoConnect token (also in localStorage) is still valid, causing 401s on every
    // API call. Trade-off: same XSS exposure as the AcceoConnect token already stored there.
    userStore: new WebStorageStateStore({ store: window.localStorage }),
    // stateStore must also be localStorage so the Acomba Desktop assertion (carried in OIDC
    // state) survives when acceo_web.exe opens the callback in a different tab or window than
    // the one that initiated the signinRedirect. sessionStorage is tab-local; localStorage is
    // per-origin and survives the cross-window/tab case.
    stateStore: new WebStorageStateStore({ store: window.localStorage }),
    // When embedded in an iframe (e.g. acombago), OIDC redirects must navigate the top-level
    // window — the identity server has frame-ancestors 'none' and blocks auth pages in frames.
    // "top" is equivalent to "self" when not in an iframe, so this is safe unconditionally.
    redirectTarget: 'top',
});

/**
 * Behind pme-factory-supervisor the app is served from an instance subdomain
 * (e.g. comptaai-pr1234.factory.pme.acceo.com) whose redirect_uri is not registered with the Identity
 * Server — and cannot be, since the supervisor exists precisely so that only its own URL needs to be.
 * The browser would go straight to /connect/authorize and be rejected, so the handshake is replaced by a
 * façade that reads the session the supervisor already established server-side.
 *
 * Every consumer of oidcUserManager is unaffected: the façade returns real `User` instances and covers the
 * whole surface this app uses (getUser, signinSilent, signinRedirect, signinCallback,
 * signoutRedirect, signoutRedirectCallback, removeUser).
 */
export const oidcUserManager = IS_SUPERVISED ? new SupervisedUserManager() : standaloneUserManager;

export type OidcUser = User;
